import produce from "immer"
import { Bounds, Circle, LatLng, LatLngBounds, Point, Polyline, Rectangle } from "leaflet"
import type { Layer, Map as LeafletMap } from "leaflet"
import i18next from "../i18n/i18nInit"
import type { MapDetailDto, MapDto, MetadataDto } from "../models"
import type { ILatLng, MapSearchResult } from "../redux"
import type { IArrow, ICircle, IPolyline, IRectangle, IShape } from "../redux/projects/projects.types"
import type { RectangleOverwriteLayer } from "./leaflet-overwrites"

export type LeafletPosition =
  | "leaflet-bottom leaflet-left"
  | "leaflet-bottom leaflet-right"
  | "leaflet-top leaflet-left"
  | "leaflet-top leaflet-right"

export const LeafletBottomLeft = "leaflet-bottom leaflet-left" as const
export const LeafletBottomRight = "leaflet-bottom leaflet-right" as const
export const LeafletTopLeft = "leaflet-top leaflet-left" as const
export const LeafletTopRight = "leaflet-top leaflet-right" as const

export function convertToLatLng(
  coords: {
    lat?: string | undefined
    lng?: string | undefined
  } = { lat: "0", lng: "0" }
): LatLng {
  return new LatLng(coords.lat ? +coords.lat : 0, coords.lng ? +coords.lng : 0)
}

export function convertLatLngInterfaceToLatLng(latLng: ILatLng): LatLng {
  return new LatLng(latLng.lat, latLng.lng)
}

export function createNewDrawingId(): string {
  let date: number = new Date().getTime()
  let performanceTime = (performance?.now && performance.now() * 1000) || 0
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character: string) => {
    let random: number = Math.random() * 16
    if (date > 0) {
      random = ((date + random) % 16) | 0
      date = Math.floor(date / 16)
    } else {
      random = ((performanceTime + random) % 16) | 0
      performanceTime = Math.floor(performanceTime / 16)
    }
    return (character === "x" ? random : (random & 0x3) | 0x8).toString(16)
  })
}

export function decodeSvg(encodedSvg: string): string {
  return atob(encodedSvg)
}

export function compareCoordinates(coord1: ILatLng, coord2: ILatLng): boolean {
  return coord1.lat === coord2.lat && coord1.lng === coord2.lng
}

export function disablePanning(map: LeafletMap): void {
  map.dragging.disable()
}

export function enablePanning(map: LeafletMap): void {
  map.dragging.enable()
}

export function disableMapInteraction(map: LeafletMap): void {
  map.scrollWheelZoom.disable()
  disablePanning(map)
}

export function enableMapInteraction(map: LeafletMap): void {
  map.scrollWheelZoom.enable()
  enablePanning(map)
}

export function layerToShape(layer: Layer): IShape {
  if (layer instanceof Circle) {
    return layerToCircle(layer as Circle)
  }
  if (layer instanceof Rectangle) {
    return layerToRectangle(layer as RectangleOverwriteLayer)
  }
  if (layer instanceof Polyline) {
    return layerToPolyline(layer as Polyline)
  }

  throw new Error("Cannot convert layer to a shape.")
}

function layerToCircle(layer: L.Circle): ICircle {
  return {
    type: "circle",
    color: layer.options.color as string,
    center: layer.getLatLng(),
    radius: layer.getRadius(),
    lineWidth: layer.options.weight || 1
  }
}

function layerToRectangle(layer: RectangleOverwriteLayer): IRectangle {
  return {
    type: "rectangle",
    color: layer.options.color as string,
    corner1: layer._corner1,
    corner2: layer._corner2,
    lineWidth: layer.options.weight || 1
  }
}

function layerToPolyline(layer: L.Polyline): IPolyline | IArrow {
  const isArrow: boolean = layer.getLatLngs().length === 2

  return isArrow
    ? {
        type: "arrow",
        color: layer.options.color as string,
        positions: layer.getLatLngs() as LatLng[],
        lineWidth: layer.options.weight || 1
      }
    : {
        type: "polyline",
        color: layer.options.color as string,
        positions: layer.getLatLngs() as LatLng[],
        lineWidth: layer.options.weight || 1,
        finished: true
      }
}

export function calculatePolylineRelativePositions(polyline: IPolyline, map: LeafletMap): Point[] {
  const points: Point[] = polyline.positions.map((latlng: LatLng) => {
    return map.latLngToLayerPoint(latlng)
  })

  const bounds: Bounds = getPolylinePixelBounds(points)
  const topLeft: Point = bounds.getTopLeft()
  const size: Point = bounds.getSize()
  const normalizedSize: Point = new Point(1 / size.x, 1 / size.y)
  const unitVector: Point = new Point(1, 1)

  return points.map((point: Point) => {
    const distanceFromCorner: Point = point.subtract(topLeft)
    return distanceFromCorner.scaleBy(normalizedSize).multiplyBy(2).subtract(unitVector)
  })
}

export function moveAndScalePolyline(
  polyline: IPolyline,
  layer: RectangleOverwriteLayer,
  relativePositions: Point[],
  map: LeafletMap
): IPolyline {
  const newPoint1: Point = map.latLngToLayerPoint(layer._corner1)
  const newPoint2: Point = map.latLngToLayerPoint(layer._corner2)
  const newTopLeft: Point = new Point(Math.min(newPoint1.x, newPoint2.x), Math.min(newPoint1.y, newPoint2.y))
  const newBottomRight: Point = new Point(Math.max(newPoint1.x, newPoint2.x), Math.max(newPoint1.y, newPoint2.y))

  const newBounds: Bounds = new Bounds(newTopLeft, newBottomRight)
  const newSize: Point = newBounds.getSize()

  const newCoordinates: LatLng[] = relativePositions.map((relativePosition: Point) => {
    return map.layerPointToLatLng(transformPoint(relativePosition, newTopLeft, newSize))
  })

  return produce(polyline, (draft: IPolyline) => {
    draft.positions = newCoordinates
  })
}

export function getPolylinePixelBounds(points: Point[]): Bounds {
  const topLeft: Point = getTopLeftFromPoints(points)
  const bottomRight: Point = getBottomRightFromPoints(points)

  return new Bounds(topLeft, bottomRight)
}

function getTopLeftFromPoints(points: Point[]): Point {
  let minX: number = Number.MAX_VALUE
  let minY: number = Number.MAX_VALUE

  for (const point of points) {
    minX = Math.min(point.x, minX)
    minY = Math.min(point.y, minY)
  }

  return new Point(minX, minY)
}

function getBottomRightFromPoints(points: Point[]): Point {
  let maxX: number = Number.MIN_VALUE
  let maxY: number = Number.MIN_VALUE

  for (const point of points) {
    maxX = Math.max(point.x, maxX)
    maxY = Math.max(point.y, maxY)
  }

  return new Point(maxX, maxY)
}

export function getPolylineBounds(points: LatLng[]): LatLngBounds {
  const northEast: LatLng = getNorthEastFromPoints(points)
  const southWest: LatLng = getSouthWestFromPoints(points)

  return new LatLngBounds(southWest, northEast)
}

function getNorthEastFromPoints(points: LatLng[]): LatLng {
  if (points.length === 0) return new LatLng(0, 0)
  const lat: number = points.reduce((previous: LatLng, current: LatLng) => {
    if (current.lat > previous.lat) {
      return current
    }
    return previous
  }).lat

  const lng: number = points.reduce((previous: LatLng, current: LatLng) => {
    if (current.lng > previous.lng) {
      return current
    }
    return previous
  }).lng

  return new LatLng(lat, lng)
}

function getSouthWestFromPoints(points: LatLng[]): LatLng {
  if (points.length === 0) return new LatLng(0, 0)
  const lat: number = points.reduce((previous: LatLng, current: LatLng) => {
    if (current.lat < previous.lat) {
      return current
    }
    return previous
  }).lat

  const lng: number = points.reduce((previous: LatLng, current: LatLng) => {
    if (current.lng < previous.lng) {
      return current
    }
    return previous
  }).lng

  return new LatLng(lat, lng)
}

export function getMapTitle(map: MapDto | MapSearchResult | MapDetailDto | MetadataDto): string {
  const prefix: string = map.region ? map.region : map.chapter ? map.chapter : ""
  return `${map.pageNumber}${map.pageSection} ${prefix.toUpperCase()} ${map.title}`
}

function transformPoint(relativePosition: Point, offset: Point, size: Point): Point {
  return relativePosition.add([1, 1]).divideBy(2).scaleBy(size).add(offset)
}

export function getPixelWidthFromBounds(bounds: LatLngBounds, map: LeafletMap, zoom?: number): number {
  const topLeft: Point = map.project(bounds.getNorthWest(), zoom)
  const bottomRight: Point = map.project(bounds.getSouthEast(), zoom)
  return bottomRight.x - topLeft.x
}

export function getPixelHeightFromBounds(bounds: LatLngBounds, map: LeafletMap, zoom?: number): number {
  const topLeft: Point = map.project(bounds.getNorthWest(), zoom)
  const bottomRight: Point = map.project(bounds.getSouthEast(), zoom)
  return bottomRight.y - topLeft.y
}

export function convertRadiusInMetersToPixels(center: LatLng, radius: number, map: LeafletMap, zoom?: number): number {
  const radiusInLatitude: number = radius / 111111
  const point1: Point = map.project(center, zoom)
  const point2: Point = map.project(new LatLng(center.lat + radiusInLatitude, center.lng), zoom)
  return Math.abs(point1.y - point2.y)
}

export function getElementScale(element: HTMLElement): [scaleX: number, scaleY: number] {
  const styles: CSSStyleDeclaration = window.getComputedStyle(element)
  const matrix: DOMMatrix = new WebKitCSSMatrix(styles.transform)
  return [matrix.m11, matrix.m22]
}

export function mapObjectDescriptorToTranslation(type: "map" | "comparison"): string {
  if (type === "map") return i18next.t("general:map")
  if (type === "comparison") return i18next.t("general:comparison")
  return ""
}
